import * as React from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { WithId } from '@common/typescript/objects/WithId';
import { Nullable, Optional } from '@common/typescript/objects/Nullable';

import { LocalizationEntry } from '@app/objects/LocalizationEntry';
import { CrematoryFeatures } from '@app/objects/CrematoryFeatures';
import { useCrematoryFeatures } from '@app/hooks/Crematory/useCrematoryFeatures';
import { ApplicationState } from '@app/store';
import {
	LocalizationEntriesRecord,
	getActionCreators,
} from '@app/store/LocalizationEntries';

interface LocalizationEntries {
	map: Map<string, LocalizationEntry>;
	list: Array<LocalizationEntry>;
	error: Nullable<string>;
	loading: boolean;

	t: TFunction;
	language: string;
	changeLanguage: (lng: string) => Promise<unknown>;
	ready: boolean;

	update: (item: Partial<LocalizationEntry> & WithId) => void;
	delete: (id: number) => void;
	create: (item: LocalizationEntry) => void;
	reload: (force?: boolean) => void;
}

interface UseLocalizationOptions {
	keyPrefix?: string;
}

function selector(state: ApplicationState, features: Optional<CrematoryFeatures>): LocalizationEntriesRecord | undefined {
	if (!features?.crematoryId) return undefined;

	return state.localizationEntries[features.crematoryId];
}

function assert(name: string) {
	return () => { throw new Error(`Function ${name} not implemented`); };
}
const emptyMap = new Map<string, LocalizationEntry>();

export function useLocalization(options?: UseLocalizationOptions): LocalizationEntries {
	const features = useCrematoryFeatures();
	const dispatch = useDispatch();
	const keyPrefix = options?.keyPrefix;
	const store = useSelector((state: ApplicationState) => selector(state, features.item));
	const { t: nativeT, i18n, ready } = useTranslation();

	React.useEffect(() => {
		if (features.isLoading) return;
		if (!features.item?.crematoryId) return;

		dispatch(getActionCreators(features.item.crematoryId).request());
	}, [features.isLoading, features.item?.crematoryId, dispatch]);

	const list = React.useMemo(
		() => (store?.map ? Array.from(store.map.values()) : []),
		[store?.map],
	);

	const t = React.useCallback(
		(key: string, interpolationOptions?: Record<string, unknown>) => {
			const baseKey = keyPrefix ? `${keyPrefix}.${key}` : key;
			const context = interpolationOptions?.context as string | undefined;
			const contextKey = context ? `${baseKey}_${context}` : undefined;
			const entry = (contextKey && store?.map?.get(contextKey)) || store?.map?.get(baseKey);

			if (entry?.value) {
				try {
					return i18n.services.interpolator.interpolate(
						entry.value,
						interpolationOptions ?? {},
						i18n.language,
						{},
					);
				} catch {
					return entry.value;
				}
			}

			return nativeT(baseKey, interpolationOptions);
		},
		[store?.map, nativeT, i18n, keyPrefix],
	) as TFunction;

	return React.useMemo(() => {
		const crematoryId = features.item?.crematoryId;

		if (!crematoryId) {
			return {
				map: emptyMap,
				list: [],
				error: null,
				loading: false,
				ready,
				language: i18n.language,
				changeLanguage: (lng: string) => i18n.changeLanguage(lng),
				t,

				update: assert('LocalizationEntries.update'),
				delete: assert('LocalizationEntries.delete'),
				create: assert('LocalizationEntries.create'),
				reload: assert('LocalizationEntries.reload'),
			};
		}

		const factory = getActionCreators(crematoryId);

		return {
			map: store?.map ?? emptyMap,
			list,
			error: store?.error ?? null,
			loading: Boolean(store && store.state !== 'idle'),
			ready,
			language: i18n.language,
			changeLanguage: (lng: string) => i18n.changeLanguage(lng),
			t,

			update: (item: Partial<LocalizationEntry> & WithId) =>
				dispatch(factory.mutate({ item, crematoryId })),
			delete: (id: number) =>
				dispatch(factory.mutate({ item: { id, deleted: true }, crematoryId })),
			create: (item: LocalizationEntry) =>
				dispatch(factory.mutate({ item, crematoryId })),
			reload: (force?: boolean) =>
				dispatch(factory.request(force)),
		};
	}, [
		features.item?.crematoryId,
		store?.map,
		store?.error,
		store?.state,
		list,
		ready,
		i18n,
		t,
		nativeT,
		dispatch,
	]);
}
